import { Modal } from '@triumph/design-system';
import DOMPurify from 'dompurify';

import { useFeatureFlags } from '@/components/shared/constants/featureFlags';
import { formatDateWithTimezone } from '@/components/shared/utils/utils';

import { useMemo } from 'react';
import { DecisionDetailProps } from '../../Applicant.types';
import {
    ModalContainer,
    StatusInfoModalContainer,
    StatusInfoModalDescription,
    StatusInfoModalHeader,
    StatusInfoModalRow,
} from './ApplicantStatus.StyledComponents';

export const StatusInfoModal = ({
    showModal,
    setShowModal,
    decisionDetail,
    productStatus,
    statusLabel,
}: {
    showModal: boolean;
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
    decisionDetail: DecisionDetailProps | undefined;
    productStatus: string;
    statusLabel?: string;
}) => {
    const { isSafeEmailViewsEnabled } = useFeatureFlags();
    const handleClose = () => setShowModal(false);
    const statusHeader = useMemo(
        () => `${productStatus} - ${statusLabel || decisionDetail?.status || ''}`,
        [productStatus, statusLabel, decisionDetail?.status],
    );
    // details may be plain text or HTML; sanitize so it renders as markup (pre-wrap keeps plain-text newlines)
    const sanitizedDetails = useMemo(
        () => (decisionDetail?.details ? DOMPurify.sanitize(decisionDetail.details) : ''),
        [decisionDetail?.details],
    );

    return (
        <Modal
            id="status-info-modal"
            headerContent=""
            visible={showModal}
            onClose={handleClose}
            bodyContent={
                <ModalContainer>
                    <StatusInfoModalContainer>
                        <StatusInfoModalHeader data-testid="status-info-header">
                            {statusHeader}
                        </StatusInfoModalHeader>
                        {decisionDetail?.details ? (
                            <StatusInfoModalRow>
                                <strong>Message:</strong>
                                {isSafeEmailViewsEnabled ? (
                                    <StatusInfoModalDescription data-testid="status-info-message">
                                        {decisionDetail.details}
                                    </StatusInfoModalDescription>
                                ) : (
                                    <StatusInfoModalDescription
                                        data-testid="status-info-message"
                                        dangerouslySetInnerHTML={{ __html: sanitizedDetails }}
                                    />
                                )}
                            </StatusInfoModalRow>
                        ) : null}
                        {decisionDetail?.reviewerEmail ? (
                            <StatusInfoModalRow>
                                <strong>Sent by:</strong>
                                <StatusInfoModalDescription data-testid="status-info-reviewer-email">
                                    {decisionDetail.reviewerEmail}
                                </StatusInfoModalDescription>
                            </StatusInfoModalRow>
                        ) : null}
                        {decisionDetail?.dateCreated ? (
                            <StatusInfoModalRow>
                                <strong>Date:</strong>
                                <StatusInfoModalDescription data-testid="status-info-date">
                                    {formatDateWithTimezone(decisionDetail.dateCreated)}
                                </StatusInfoModalDescription>
                            </StatusInfoModalRow>
                        ) : null}
                    </StatusInfoModalContainer>
                </ModalContainer>
            }
            primaryButtonText="Close"
            onPrimaryAction={handleClose}
        />
    );
};
